import React, { useRef, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { 
  Stack,
  Spinner, 
  SpinnerSize, 
  } from "@fluentui/react";
import chatStyles from "../chat/Chat.module.css";
import styles from "./Share.module.css";
import {
  ChatMessage,
  Citation,
  historyReadShared,
  historyReadFull,
  getFrontEndSettings,
} from "../../api";
import { usePromptTemplates } from "../../context/PromptTemplateContext";
import { stripTemplateContent } from "../../util/promptTemplateUtils";
import Layout from "../layout/Layout";
import { ChatMessageContainer } from "../../components/ChatMessageContainer/ChatMessageContainer";
import { CitationPanel } from "../../components/CitationPanel/CitationPanel";

const Share = () => {
  const { templates, loading: templatesLoading } = usePromptTemplates();

  // Get the conversation ID from URL parameters
  const { conversationId: urlConversationId } = useParams<{ conversationId: string }>();

  const [conversationTitle, setConversationTitle] = useState<string>("");
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const [activeCitation, setActiveCitation] = useState<Citation>();
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
  const [rawAnswers, setRawAnswers] = useState<ChatMessage[]>([]);
  const [selectedConvId, setSelectedConvId] = useState<string>("");

  const answers = useMemo(
    () => stripTemplateContent(rawAnswers, templates),
    [rawAnswers, templates]
  );
  const [fetchingConvMessages, setFetchingConvMessages] = React.useState(false);
  const [isInitialAPItriggered, setIsInitialAPItriggered] = useState(false);

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation);
    setIsCitationPanelOpen(true);
  };

  const onSelectConversation = async (id: string) => {

    if (!id) {
      console.error("No conversation Id found");
      return;
    }

    setFetchingConvMessages(true);
    
    // Try token-based shared link first, fall back to legacy GUID-based reading
    const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    const conversation = isGuid
      ? await historyReadFull(id)
      : await historyReadShared(id);
    
    setConversationTitle(conversation.title);
    setRawAnswers(conversation.messages);
    setFetchingConvMessages(false);
    setSelectedConvId(conversation.conversation_id || id);
  };

  useEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: "instant" });
  }, [selectedConvId]);

  useEffect(() => {
    setIsInitialAPItriggered(true); // eslint-disable-line react-hooks/set-state-in-effect
  }, []);

  useEffect(() => {
    if (isInitialAPItriggered) {
      (async () => {
        const response = await getFrontEndSettings();
        if (response.CHAT_HISTORY_ENABLED) {
          onSelectConversation(urlConversationId!);          
        }
      })();
    }
  }, [isInitialAPItriggered]); // eslint-disable-line react-hooks/exhaustive-deps

  const showCitationPanel = answers.length > 0 && isCitationPanelOpen && activeCitation;
  // Show loading state while templates are still being fetched to prevent
  // raw template content from flashing before stripTemplateContent can run.
  const isLoading = fetchingConvMessages || (rawAnswers.length > 0 && templatesLoading);
  return (
    <Layout>
      <div className={chatStyles.container}>
        <Stack horizontal className={chatStyles.chatRoot}>
          <div
            className={`${chatStyles.chatContainer} ${chatStyles.MobileChatContainer}`}
          >    
              {selectedConvId === "" || isLoading ?
                (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingContainer}>
                      <Spinner size={SpinnerSize.large} label="Loading conversation..." />
                    </div>
                  </div>
                )
              : answers.length === 0 ? 
                (
                  <div className={styles.invalidConversationMessage}>
                    <Stack>
                      <Stack.Item>
                        <h1>This conversation link is no longer available</h1>
                      </Stack.Item>
                      <Stack.Item>
                        <p>The conversation you are trying to view has either expired, been revoked, or does not exist.</p>
                      </Stack.Item>
                    </Stack>
                  </div>
                ) :              
                (
                  <div className={chatStyles.chatMessageStream}>
                    <ChatMessageContainer
                      conversationId={selectedConvId}
                      activeCardIndex={0}
                      answers={answers}
                      fetchingConvMessages={fetchingConvMessages}
                      handleSpeech={() => { }}
                      onShowCitation={onShowCitation}
                      onFeedbackUpdate={() => { }}                      
                      isReadOnly={ true }
                    />              
                    <div data-testid="streamendref-id" ref={chatMessageStreamEnd} />
                  </div>
                ) }
          
          </div>
          {showCitationPanel && (
            <CitationPanel
              activeCitation={activeCitation}
              setIsCitationPanelOpen={setIsCitationPanelOpen}
            />
          )}
        </Stack>
      </div>
    </Layout>
  );
};

export default Share;
